import { useEffect, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import CustomButton from "@/components/customButton";
import { useTypedSelector } from "@/hooks";
import { useDispatch } from "react-redux";
import {
  useFetchEmailTemplateQuery,
  useUpdateEmailTemplateMutation,
} from "@/services";
import { message } from "antd";

import { CloseOutlined } from "@ant-design/icons";
import customErrorMessage from "@/components/CustomErrorMessage";

const EmailTemplate = () => {
  const dispatch = useDispatch();
  // Extract company ID safely from authenticated user
  const companyId = useTypedSelector(state => state.auth?.user?.company?.id);
  // Inside your component
  const [activeEditor, setActiveEditor] = useState("start");
  const [emailStart, setEmailStart] = useState("");
  const [emailEnd, setEmailEnd] = useState("");
  const [ccEmails, setCcEmails] = useState<string[]>([]);
  const [newCcEmail, setNewCcEmail] = useState("");
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  // Fetch from API
  const {
    data: emailTemplate,
    isLoading,
    error,
  } = useFetchEmailTemplateQuery(companyId, {
    skip: !companyId,
  });

  const [updateEmailTemplate, { isLoading: isUpdating }] =
    useUpdateEmailTemplateMutation();
  // Update editor state when API data is fetched
  useEffect(() => {
    if (emailTemplate?.data) {
      setEmailStart(emailTemplate.data.email_body_start || "");
      setEmailEnd(emailTemplate.data.email_body_end || "");
      setCcEmails(emailTemplate.data.cc_emails || []);
      setLogoPreview(emailTemplate.data.logo_url || null);
    }
  }, [emailTemplate]);

  const handleSaveTemplate = async () => {
    try {
      await updateEmailTemplate({
        companyId: companyId,
        body: {
          email_body_start: emailStart,
          email_body_end: emailEnd,
          cc_emails: ccEmails,
          company_logo: logoPreview,
        },
      }).unwrap();

      message.success("Email template updated successfully!");
    } catch (err) {
      customErrorMessage("Failed to update the email template!");
    }
  };

  // const modules = {
  //   toolbar: [
  //     [{ header: [1, 2, false] }],
  //     ["bold", "italic", "underline"],
  //     [{ list: "ordered" }, { list: "bullet" }],
  //     ["link"],
  //   ],
  // };
  const modules = {
    toolbar: {
      container: "#email-toolbar",
    },
  };
  return (

    <div className="bg-white p-4 sm:p-5">
      <div className="jobCreationBar bg-revampBlue px-4 py-6 rounded-lg flex justify-between items-center">
        <div className="title text-white font-medium text-3xl uppercase">
          Email Template
        </div>

        <div className="flex flex-row justify-between gap-2">

          <CustomButton
            isLoading={false}
            onClick={() => setActiveEditor("start")}
            btnName="Email Introduction"
            customClasses={`shadow-md font-medium border py-1 px-6 !border-themeBorder ${activeEditor === "start"
              ? "bg-gray-800 text-white"
              : "text-black"
              }`}
            iconPosition="left"
          />
          <CustomButton
            isLoading={false}
            onClick={() => setActiveEditor("end")}
            btnName="Email Conclusion"
            customClasses={`shadow-md font-medium border py-1 px-6 !border-themeBorder ${activeEditor === "end"
              ? "bg-gray-800 text-white"
              : "text-black"
              }`}
            iconPosition="left"
          />

          <CustomButton
            isLoading={false}
            onClick={() => setActiveEditor("settings")}
            btnName="Settings"
            customClasses={`shadow-md font-medium border py-1 px-6 !border-themeBorder ${activeEditor === "settings"
              ? "bg-gray-800 text-white"
              : "text-black"
              }`}
            iconPosition="left"
          />

        </div>
      </div>

      <div className="jobCreationListing mt-6 bg-revampBlue px-14 py-4 rounded-lg">
        <div className="my-6">


          <div className="my-6">
            {/* <h3 className="text-xl font-semibold mb-2">
              {activeEditor === "start"
                ? "Email Introduction"
                : activeEditor === "end"
                  ? "Email Conclusion"
                  : "Settings"}
            </h3> */}

            <div className="tools inline-block w-fit py-3">

              <div
                id="email-toolbar"
                className="rounded bg-white px-3"
              >
                <select className="ql-header" defaultValue="">
                  <option value="1"></option>
                  <option value="2"></option>
                  <option value=""></option>
                </select>

                <button className="ql-bold"></button>
                <button className="ql-italic"></button>
                <button className="ql-underline"></button>

                <button className="ql-list" value="ordered"></button>
                <button className="ql-list" value="bullet"></button>

                <button className="ql-link"></button>
              </div>
            </div>

            <div className="border-2 rounded-lg overflow-hidden bg-white">
              {activeEditor === "start" ? (
                <ReactQuill
                  key="start-editor"
                  theme="snow"
                  value={emailStart}
                  onChange={setEmailStart}
                  modules={modules}
                  className="h-64 border-none"
                />
              ) : activeEditor === "end" ? (
                <ReactQuill
                  key="end-editor"
                  theme="snow"
                  value={emailEnd}
                  onChange={setEmailEnd}
                  modules={modules}
                  className="h-64 border-none"
                />
              ) : (
                <div className="space-y-6 p-4">
                  {/* Upload Logo Section */}
                  <div>
                    <label className="block text-lg font-semibold text-gray-800 mb-2">
                      Company Logo
                    </label>
                    {logoPreview ? (
                      <div className="relative inline-block w-40 h-40">
                        <img
                          src={logoPreview}
                          alt="Logo Preview"
                          className="w-full h-full object-contain border rounded shadow"
                        />
                        <CloseOutlined
                          onClick={() => {
                            setLogoFile(null);
                            setLogoPreview(null);
                          }}
                          className="absolute top-0 right-0 bg-white rounded-full p-1 shadow cursor-pointer text-red-500 hover:text-red-700"
                        />
                      </div>
                    ) : (
                      <input
                        type="file"
                        accept="image/*"
                        onChange={e => {
                          const file = e.target.files?.[0];
                          if (file) {
                            setLogoFile(file);
                            const reader = new FileReader();
                            reader.onloadend = () => {
                              setLogoPreview(reader.result as string);
                            };
                            reader.readAsDataURL(file);
                          }
                        }}
                        className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:ring-2 focus:ring-themeBlue"
                      />
                    )}
                  </div>

                  {/* CC Emails Section */}
                  <label className="block text-lg font-semibold text-gray-800">
                    CC Emails
                  </label>

                  <div className="flex flex-wrap gap-2">
                    {ccEmails.map((email, index) => (
                      <div
                        key={index}
                        className="flex items-center bg-blue-100 text-themeBlue px-3 py-1 rounded-full shadow-sm"
                      >
                        <span className="mr-2 text-sm">{email}</span>
                        <CloseOutlined
                          onClick={() =>
                            setCcEmails(prev =>
                              prev.filter((_, i) => i !== index)
                            )
                          }
                          className="text-themeBlue hover:text-red-500 cursor-pointer text-xs"
                        />
                      </div>
                    ))}
                  </div>

                  <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center">
                    <input
                      type="email"
                      placeholder="Enter email"
                      value={newCcEmail}
                      onChange={e => setNewCcEmail(e.target.value)}
                      className="flex-1 border border-gray-300 rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-themeBlue"
                    />
                    <button
                      onClick={() => {
                        if (
                          newCcEmail &&
                          /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newCcEmail)
                        ) {
                          if (!ccEmails.includes(newCcEmail)) {
                            setCcEmails([...ccEmails, newCcEmail]);
                            setNewCcEmail("");
                          } else {
                            message.warning("This email is already added.");
                          }
                        } else {
                          customErrorMessage("Please enter a valid email address.");
                        }
                      }}
                      className="bg-revampBlue hover:bg-revampDarkBlue text-white px-6 py-2 rounded w-full md:w-auto"
                    >
                      Add Email
                    </button>
                  </div>
                </div>
              )}
            </div>

            {activeEditor != "settings" && (
              <p className="mt-4 text-white">
                You can includes following #hashtags tags in your email
                template.
              </p>
            )}
          </div>
        </div>


      </div>
      <div className="flex justify-center mt-5 mx-auto">
        <button
          className={`bg-revampOrange font-semibold text-white text-base py-2 px-10 rounded-md ${isUpdating ? "opacity-50 cursor-not-allowed" : ""
            }`}
          onClick={handleSaveTemplate}
          disabled={isUpdating}
        >
          {isUpdating ? "Loading..." : "Save Template"}
        </button>
      </div>


    </div>

  );
};

export default EmailTemplate;

// import { useEffect, useState } from "react";
// import ReactQuill from "react-quill";
// import "react-quill/dist/quill.snow.css";
// import { CloseOutlined } from "@ant-design/icons";
// import { message } from "antd";

// import CustomButton from "@/components/customButton";
// import customErrorMessage from "@/components/CustomErrorMessage";
// import { useTypedSelector } from "@/hooks";
// import {
//   useFetchEmailTemplateQuery,
//   useUpdateEmailTemplateMutation,
// } from "@/services";

// type EditorType = "start" | "end" | "settings";

// const EmailTemplate = () => {
//   const companyId = useTypedSelector(
//     state => state.auth?.user?.company?.id
//   );

//   const [activeEditor, setActiveEditor] =
//     useState<EditorType>("start");

//   const [emailStart, setEmailStart] = useState("");
//   const [emailEnd, setEmailEnd] = useState("");

//   const [ccEmails, setCcEmails] = useState<string[]>([]);
//   const [newCcEmail, setNewCcEmail] = useState("");

//   const [logoPreview, setLogoPreview] = useState<string | null>(
//     null
//   );

//   const { data: emailTemplate, isLoading } =
//     useFetchEmailTemplateQuery(companyId, {
//       skip: !companyId,
//     });

//   const [updateEmailTemplate, { isLoading: isUpdating }] =
//     useUpdateEmailTemplateMutation();

//   useEffect(() => {
//     if (!emailTemplate?.data) return;

//     setEmailStart(
//       emailTemplate.data.email_body_start || ""
//     );

//     setEmailEnd(
//       emailTemplate.data.email_body_end || ""
//     );

//     setCcEmails(
//       emailTemplate.data.cc_emails || []
//     );

//     setLogoPreview(
//       emailTemplate.data.logo_url || null
//     );
//   }, [emailTemplate]);

//   const modules = {
//     toolbar: [
//       [{ header: [1, 2, false] }],
//       ["bold", "italic", "underline"],
//       [{ list: "ordered" }, { list: "bullet" }],
//       ["link"],
//     ],
//   };

//   const handleEditorChange = (value: string) => {
//     if (activeEditor === "start") {
//       setEmailStart(value);
//       return;
//     }

//     if (activeEditor === "end") {
//       setEmailEnd(value);
//     }
//   };

//   const editorValue =
//     activeEditor === "start" ? emailStart : emailEnd;

//   const handleLogoChange = (
//     event: React.ChangeEvent<HTMLInputElement>
//   ) => {
//     const file = event.target.files?.[0];

//     if (!file) return;

//     const reader = new FileReader();

//     reader.onloadend = () => {
//       setLogoPreview(reader.result as string);
//     };

//     reader.readAsDataURL(file);
//   };

//   const handleAddEmail = () => {
//     const cleanedEmail = newCcEmail.trim().toLowerCase();

//     const validEmail =
//       /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanedEmail);

//     if (!validEmail) {
//       customErrorMessage(
//         "Please enter a valid email address."
//       );
//       return;
//     }

//     if (ccEmails.includes(cleanedEmail)) {
//       message.warning("This email is already added.");
//       return;
//     }

//     setCcEmails(previousEmails => [
//       ...previousEmails,
//       cleanedEmail,
//     ]);

//     setNewCcEmail("");
//   };

//   const handleRemoveEmail = (emailIndex: number) => {
//     setCcEmails(previousEmails =>
//       previousEmails.filter(
//         (_, index) => index !== emailIndex
//       )
//     );
//   };

//   const handleSaveTemplate = async () => {
//     if (!companyId) {
//       customErrorMessage("Company information is missing.");
//       return;
//     }

//     try {
//       await updateEmailTemplate({
//         companyId,
//         body: {
//           email_body_start: emailStart,
//           email_body_end: emailEnd,
//           cc_emails: ccEmails,
//           company_logo: logoPreview,
//         },
//       }).unwrap();

//       message.success(
//         "Email template updated successfully!"
//       );
//     } catch (error) {
//       customErrorMessage(
//         "Failed to update the email template!"
//       );
//     }
//   };

//   return (
//     <div className="bg-white p-4 sm:p-5">
//       {/* Header and action panel */}
//       <div className="bg-revampBlue rounded-lg px-6 py-4 flex items-center justify-between">
//         <h1 className="text-white text-xl font-semibold uppercase">
//           Email Template
//         </h1>

//         <div className="flex items-center">
//           <CustomButton
//             isLoading={false}
//             onClick={() => setActiveEditor("start")}
//             btnName="Email Introduction"
//             customClasses={
//               "shadow font-medium py-2 px-6 mr-4 " +
//               (activeEditor === "start"
//                 ? "bg-revampDarkBlue text-white"
//                 : "bg-white text-gray-700")
//             }
//             iconPosition="left"
//           />

//           <CustomButton
//             isLoading={false}
//             onClick={() => setActiveEditor("end")}
//             btnName="Email Conclusion"
//             customClasses={
//               "shadow font-medium border py-2 px-6 mr-4 " +
//               (activeEditor === "end"
//                 ? "bg-revampDarkBlue text-white"
//                 : "bg-white text-gray-700")
//             }
//             iconPosition="left"
//           />

//           <CustomButton
//             isLoading={false}
//             onClick={() => setActiveEditor("settings")}
//             btnName="Settings"
//             customClasses={
//               "shadow font-medium border py-2 px-10 " +
//               (activeEditor === "settings"
//                 ? "bg-revampDarkBlue text-white"
//                 : "bg-white text-gray-700")
//             }
//             iconPosition="left"
//           />
//         </div>
//       </div>

//       {/* Editor panel */}
//       {activeEditor !== "settings" && (
//         <div className="mt-6 bg-revampBlue rounded-lg px-5 py-5">
//           <div className="bg-white rounded-lg overflow-hidden">
//             {isLoading ? (
//               <div className="h-64 flex items-center justify-center text-gray-500">
//                 Loading email template...
//               </div>
//             ) : (
//               <ReactQuill
//                 theme="snow"
//                 value={editorValue}
//                 onChange={handleEditorChange}
//                 modules={modules}
//                 placeholder={
//                   activeEditor === "start"
//                     ? "Email Introduction"
//                     : "Email Conclusion"
//                 }
//                 style={{
//                   minHeight: "290px",
//                   backgroundColor: "#ffffff",
//                 }}
//               />
//             )}
//           </div>

//           <p className="mt-4 text-white text-sm">
//             You can include the following #hashtags in your
//             email template.
//           </p>
//         </div>
//       )}

//       {/* Settings panel */}
//       {activeEditor === "settings" && (
//         <div className="mt-6 bg-revampBlue rounded-lg px-6 py-6">
//           <div className="bg-white rounded-lg p-6">
//             <div className="flex items-start">
//               {/* Company logo */}
//               <div className="w-1/2 pr-8">
//                 <label className="block text-lg font-semibold text-gray-800 mb-3">
//                   Company Logo
//                 </label>

//                 {logoPreview ? (
//                   <div className="relative inline-block w-40 h-40">
//                     <img
//                       src={logoPreview}
//                       alt="Company logo"
//                       className="w-full h-full object-contain border rounded shadow"
//                     />

//                     <CloseOutlined
//                       onClick={() => setLogoPreview(null)}
//                       className="absolute top-0 right-0 bg-white rounded-full p-1 shadow cursor-pointer text-red-500"
//                     />
//                   </div>
//                 ) : (
//                   <input
//                     type="file"
//                     accept="image/*"
//                     onChange={handleLogoChange}
//                     className="w-full  border-gray-300 px-4 py-3 rounded focus:outline-none"
//                   />
//                 )}
//               </div>

//               {/* CC emails */}
//               <div className="w-1/2 pl-8 border-l border-gray-200">
//                 <label className="block text-lg font-semibold text-gray-800 mb-3">
//                   CC Emails
//                 </label>

//                 <div className="flex flex-wrap mb-4">
//                   {ccEmails.map((email, index) => (
//                     <div
//                       key={`${email}-${index}`}
//                       className="flex items-center bg-blue-100 text-revampBlue px-3 py-2 rounded-full shadow-sm mr-2 mb-2"
//                     >
//                       <span className="mr-2 text-sm">
//                         {email}
//                       </span>

//                       <CloseOutlined
//                         onClick={() =>
//                           handleRemoveEmail(index)
//                         }
//                         className="cursor-pointer text-xs"
//                       />
//                     </div>
//                   ))}
//                 </div>

//                 <div className="flex items-center">
//                   <input
//                     type="email"
//                     placeholder="Enter email"
//                     value={newCcEmail}
//                     onChange={event =>
//                       setNewCcEmail(event.target.value)
//                     }
//                     onKeyDown={event => {
//                       if (event.key === "Enter") {
//                         event.preventDefault();
//                         handleAddEmail();
//                       }
//                     }}
//                     className="flex-1  rounded px-4 py-3 focus:outline-none"
//                   />

//                   <button
//                     type="button"
//                     onClick={handleAddEmail}
//                     className="bg-revampBlue text-white px-6 py-3 rounded ml-3"
//                   >
//                     Add Email
//                   </button>
//                 </div>
//               </div>
//             </div>
//           </div>
//         </div>
//       )}

//       {/* Save button */}
//       <div className="flex justify-center mt-8">
//         <button
//           type="button"
//           onClick={handleSaveTemplate}
//           disabled={isUpdating}
//           className={
//             "bg-revampOrange text-white text-base py-3 px-16 rounded-md font-semibold " +
//             (isUpdating
//               ? "opacity-50 cursor-not-allowed"
//               : "cursor-pointer")
//           }
//         >
//           {isUpdating ? "Saving..." : "Save Template"}
//         </button>
//       </div>
//     </div>
//   );
// };

// export default EmailTemplate;
